import React from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Container,
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import HomeIcon from '@mui/icons-material/Home';
import { useTheme } from '@mui/material/styles';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details
    console.error('🚨 ERROR BOUNDARY TRIGGERED:', {
      error: error.toString(),
      stack: error.stack,
      errorInfo: errorInfo,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      localStorage: {
        selectedHotel: localStorage.getItem('selectedHotel'),
        hotelPassword: localStorage.getItem('hotelPassword') ? '[SET]' : '[NOT SET]',
        sessionId: localStorage.getItem('tabbleSessionId'),
      }
    });

    this.setState({
      error: error,
      errorInfo: errorInfo,
    });

    // You can also log the error to an error reporting service here
    if (process.env.NODE_ENV === 'production') {
      // Log to error reporting service
      console.error('Production error:', {
        error: error.toString(),
        errorInfo: errorInfo.componentStack,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href,
      });
    }
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      const { fallback: Fallback } = this.props;

      // If a custom fallback component is provided, use it
      if (Fallback) {
        return (
          <Fallback
            error={this.state.error}
            errorInfo={this.state.errorInfo}
            onRetry={this.handleRetry}
            onGoHome={this.handleGoHome}
          />
        );
      }

      // Default error UI
      return (
        <Container maxWidth="md" sx={{ mt: 8 }}>
          <Paper
            elevation={3}
            sx={{
              p: 4,
              textAlign: 'center',
              backgroundColor: this.props.theme.palette.background.paper,
              border: '1px solid rgba(255, 165, 0, 0.3)',
              borderRadius: '12px',
            }}
          >
            <Box sx={{ mb: 3 }}>
              <ErrorOutlineIcon
                sx={{
                  fontSize: '4rem',
                  color: '#FF385C',
                  mb: 2,
                }}
              />
              <Typography
                variant="h4"
                component="h1"
                gutterBottom
                sx={{ color: this.props.theme.palette.text.primary, fontWeight: 'bold' }}
              >
                Oops! Something went wrong
              </Typography>
              <Typography
                variant="h6"
                sx={{ color: '#FFA500', mb: 3 }}
              >
                We're sorry for the inconvenience. Please try again.
              </Typography>
            </Box>

            {/* Show error details for debugging Vercel deployment issues */}
            {this.state.error && (
              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  backgroundColor: 'rgba(255, 0, 0, 0.1)',
                  border: '1px solid rgba(255, 0, 0, 0.3)',
                  borderRadius: '8px',
                  textAlign: 'left',
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ color: '#FF385C', fontWeight: 'bold', mb: 1 }}
                >
                  Error Details (Debug Mode):
                </Typography>
                <Typography
                  variant="body2"
                  component="pre"
                  sx={{
                    color: this.props.theme.palette.text.primary,
                    fontSize: '0.8rem',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    maxHeight: '300px',
                    overflow: 'auto',
                  }}
                >
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </Typography>
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={this.handleRetry}
                sx={{
                  backgroundColor: '#FFA500',
                  color: '#000000',
                  fontWeight: 'bold',
                  '&:hover': {
                    backgroundColor: '#E69500',
                  },
                }}
              >
                Try Again
              </Button>
              <Button
                variant="outlined"
                startIcon={<HomeIcon />}
                onClick={this.handleGoHome}
                sx={{
                  borderColor: '#FFA500',
                  color: '#FFA500',
                  '&:hover': {
                    borderColor: '#E69500',
                    backgroundColor: 'rgba(255, 165, 0, 0.1)',
                  },
                }}
              >
                Go Home
              </Button>
            </Box>
          </Paper>
        </Container>
      );
    }

    return this.props.children;
  }
}

// Higher-order component for functional components
export const withErrorBoundary = (Component, fallback) => {
  return function WithErrorBoundaryComponent(props) {
    return (
      <ErrorBoundary fallback={fallback}>
        <Component {...props} />
      </ErrorBoundary>
    );
  };
};

// Theme-aware wrapper (ErrorBoundary is a class component, so inject theme as a prop)
const ThemedErrorBoundary = (props) => {
  const theme = useTheme();
  return <ErrorBoundary {...props} theme={theme} />;
};

export default ThemedErrorBoundary;
